import {
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  HStack,
  Spacer,
  Tag,
  TagLabel,
  Accordion,
  Flex,
  TagLeftIcon,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState } from 'react';

import {
  finleyAssert,
  finleyAssertUnreachable,
  date as commonDate,
} from '@finley-technologies/common';

import {
  UserApprovalGroupGQLType,
  UserApprovalStateGQLType,
  WorkflowStringArrayComparatorGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

const _getComparatorText = (
  comparator: WorkflowStringArrayComparatorGQLType,
) => {
  switch (comparator) {
    case 'CONTAINS_ALL_OF':
      return 'All of: ';
    case 'CONTAINS_ANY_OF':
      return 'Any of: ';
    default:
      return finleyAssertUnreachable(comparator);
  }
};

const UserApprovalConfirmationModal = ({
  header,
  body,
  confirmButtonText,
  onConfirm,
  loading,
  onClose,
}: {
  header: string;
  body: string;
  confirmButtonText: string;
  onConfirm: () => Promise<void>;
  loading: boolean;
  onClose: () => void;
}) => {
  const cancelRef = React.useRef(null);

  return (
    <AlertDialog leastDestructiveRef={cancelRef} isOpen onClose={onClose}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {header}
          </AlertDialogHeader>

          <AlertDialogBody>{body}</AlertDialogBody>

          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              onClick={onClose}
              colorScheme="aurora"
              variant="outline"
              isLoading={loading}
            >
              Cancel
            </Button>
            <Button
              isLoading={loading}
              colorScheme="aurora"
              variant="solid"
              onClick={onConfirm}
              ml={3}
            >
              {confirmButtonText}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

const ApprovalStatus = ({
  approvalState,
  isApprovalGroupActionable,
}: {
  // Approval state for a single user. An approval group can contain multiple users.
  // Not all of them will have a `userApproval` record yet.
  approvalState: UserApprovalStateGQLType | undefined;
  isApprovalGroupActionable: boolean;
}) => {
  if (!approvalState) {
    return isApprovalGroupActionable ? (
      <Typography variant="detail" trim>
        Awaiting Approval
      </Typography>
    ) : null;
  }

  if (
    approvalState.status === 'approved' ||
    approvalState.status === 'rejected' ||
    (approvalState.status === 'requested' && isApprovalGroupActionable)
  ) {
    return (
      <Typography variant="detail" trim>
        {_.startCase(approvalState.status)} on{' '}
        {commonDate.epochToLocaleDateString(approvalState.statusAt)}
      </Typography>
    );
  }
  return null;
};

const ApprovalButtons = ({
  userId,
  loading,
  onAction,
}: {
  userId: string;
  loading: boolean;
  onAction: (params: { action: 'approve' | 'reject'; userId: string }) => void;
}) => (
  <Flex gap="10px">
    <Button
      variant="secondary"
      colorScheme="v2-aurora"
      leftIcon={<Icon icon="xmark" />}
      isLoading={loading}
      onClick={() => onAction({ action: 'reject', userId })}
    >
      Reject
    </Button>
    <Button
      variant="secondary"
      colorScheme="v2-aurora"
      leftIcon={<Icon icon="check" />}
      isLoading={loading}
      onClick={() => onAction({ action: 'approve', userId })}
    >
      Approve
    </Button>
  </Flex>
);

const RequestApprovalButtons = ({
  userId,
  loading,
  onAction,
}: {
  userId: string;
  loading: boolean;
  onAction: (params: { action: 'request'; userId: string }) => void;
}) => (
  <Button
    variant="secondary"
    colorScheme="v2-aurora"
    leftIcon={<Icon icon="paper-plane-top" />}
    isLoading={loading}
    onClick={() => onAction({ action: 'request', userId })}
  >
    Send Email
  </Button>
);

const UserApprovalGroup = ({
  approvalGroup,
  currentUserId,
  approveParams,
  rejectParams,
  requestApprovalParams,
  isActionable,
}: {
  approvalGroup: UserApprovalGroupGQLType;
  currentUserId: string;
  approveParams: InternalApprovalAction;
  rejectParams: InternalApprovalAction;
  requestApprovalParams: InternalApprovalAction;
  isActionable: boolean;
}) => {
  const [confirmationModal, setConfirmationModal] = useState<{
    action: 'approve' | 'reject' | 'request';
    userId: string;
  }>();
  const modalAction =
    // eslint-disable-next-line no-nested-ternary
    confirmationModal?.action === 'approve'
      ? approveParams
      : // eslint-disable-next-line no-nested-ternary
      confirmationModal?.action === 'reject'
      ? rejectParams
      : confirmationModal?.action === 'request'
      ? requestApprovalParams
      : undefined;

  const [loading, setLoading] = useState<boolean>(false);

  // TODO(jon): we should have an explicit node/setting that indicates
  //  auto-approval, rather than assuming ALL_OF + empty userId list means
  //  auto-approve.
  let autoApproved = false;
  switch (approvalGroup.comparator) {
    case 'CONTAINS_ANY_OF':
      finleyAssert(
        approvalGroup.requestedApprovals.length > 0,
        `[Workflows] Cannot have a UserApprovalGroup with no users to approve.`,
      );
      break;
    case 'CONTAINS_ALL_OF':
      if (
        approvalGroup.requestedApprovals.length === 0 &&
        approvalGroup.groupState?.status === 'approved'
      ) {
        autoApproved = true;
      }
      break;
    default:
      finleyAssertUnreachable(approvalGroup.comparator);
  }

  const allUsersNameText = approvalGroup.requestedApprovals
    .map(
      (u) => `${_.startCase(u.user.firstName)} ${_.startCase(u.user.lastName)}`,
    )
    .join(', ');

  const comparatorText =
    approvalGroup.requestedApprovals.length === 1
      ? ``
      : _getComparatorText(approvalGroup.comparator);

  const title = autoApproved
    ? 'Approval not required.'
    : `${comparatorText}${allUsersNameText} must approve.`;

  return (
    <>
      <AccordionItem width="full">
        <AccordionButton
          _expanded={{ borderBottomWidth: '1px' }}
          bgColor={colors.background['50']}
          paddingY={0}
          paddingLeft={0}
          paddingRight="8px"
        >
          <HStack padding="16px" width="full">
            <Typography variant="detail" trim>
              {title}
            </Typography>
            <Spacer />
            {/* eslint-disable-next-line no-nested-ternary, no-nested-ternary */}
            {approvalGroup.groupState?.status === 'approved' ? (
              <>
                <Typography variant="detail" trim fontWeight="semibold">
                  {`Approved ${commonDate.epochToLocaleDateString(
                    approvalGroup.groupState.statusAt,
                  )}`}
                </Typography>
                <Tag variant="solid-muted" colorScheme="green">
                  <TagLeftIcon>
                    <Icon icon="circle-check" />
                  </TagLeftIcon>
                  <TagLabel>Approved</TagLabel>
                </Tag>
              </>
            ) : // eslint-disable-next-line no-nested-ternary
            approvalGroup.groupState?.status === 'rejected' ? (
              <Tag variant="solid-muted" colorScheme="red">
                <TagLeftIcon>
                  <Icon icon="circle-x" />
                </TagLeftIcon>
                <TagLabel>Rejected</TagLabel>
              </Tag>
            ) : isActionable ? (
              <></> // if actionable do not show "requires prior approval"
            ) : (
              <Tag variant="solid-muted" colorScheme="orange">
                <TagLeftIcon>
                  <Icon icon="circle-exclamation" />
                </TagLeftIcon>
                <TagLabel>Requires Prior Approval</TagLabel>
              </Tag>
            )}
          </HStack>
          <AccordionIcon />
        </AccordionButton>
        <AccordionPanel pb={4} padding="0">
          {approvalGroup.requestedApprovals.map((requestedApproval, index) => {
            // eslint-disable-next-line finley/variable-length
            const u = requestedApproval.user;
            const isCurrentUser = u.id === currentUserId;

            return (
              <HStack
                key={u.id}
                padding="16px"
                borderBottomWidth={
                  index === approvalGroup.requestedApprovals.length - 1
                    ? '0px'
                    : '1px'
                }
              >
                <Typography variant="detail" trim>{`${_.startCase(
                  u.firstName,
                )} ${_.startCase(u.lastName)}`}</Typography>
                <Spacer />

                <ApprovalStatus
                  approvalState={requestedApproval.state ?? undefined}
                  isApprovalGroupActionable={isActionable}
                />
                {isActionable && isCurrentUser && (
                  <ApprovalButtons
                    userId={u.id}
                    loading={loading}
                    onAction={setConfirmationModal}
                  />
                )}
                {isActionable &&
                  !isCurrentUser &&
                  requestedApproval.state?.status !== 'requested' && (
                    <RequestApprovalButtons
                      userId={u.id}
                      loading={loading}
                      onAction={setConfirmationModal}
                    />
                  )}
              </HStack>
            );
          })}
        </AccordionPanel>
      </AccordionItem>

      {/* action confirmation modal */}
      {confirmationModal && modalAction && (
        <UserApprovalConfirmationModal
          header={modalAction.confirmationTitle}
          body={modalAction.confirmationText}
          confirmButtonText={modalAction.confirmationTitle}
          loading={loading}
          onClose={() => {
            setConfirmationModal(undefined);
          }}
          onConfirm={async () => {
            if (loading) {
              return;
            }

            try {
              setLoading(true);
              await modalAction.onPerformAction({
                userId: confirmationModal.userId,
              });
              setConfirmationModal(undefined);
            } finally {
              setLoading(false);
            }
          }}
        />
      )}
    </>
  );
};

type InternalApprovalAction = {
  confirmationTitle: string;
  confirmationText: string;
  onPerformAction: (params: { userId: string }) => Promise<void>;
};

export const InternalApprovalInstance = ({
  approvalGroups,
  approveParams,
  rejectParams,
  requestApprovalParams,
  readOnly = false,
}: {
  approvalGroups: UserApprovalGroupGQLType[];
  approveParams: InternalApprovalAction;
  rejectParams: InternalApprovalAction;
  requestApprovalParams: InternalApprovalAction;
  readOnly?: boolean;
}) => {
  const { user } = useUserAuthOrThrow();

  // No groupState implies pending.
  const firstPendingGroupIndex = approvalGroups.findIndex(
    (group) => !group.groupState,
  );

  const isOverallRejected = approvalGroups.some(
    (group) => group.groupState?.status === 'rejected',
  );
  const isOverallActionable = !readOnly && !isOverallRejected;

  return (
    <Accordion
      width="full"
      defaultIndex={
        firstPendingGroupIndex !== -1 ? [firstPendingGroupIndex] : []
      }
      allowMultiple
      borderLeftWidth="1px"
      borderRightWidth="1px"
    >
      {approvalGroups.map((approvalGroup, index) => {
        const isFirstPendingGroup = index === firstPendingGroupIndex;
        const isGroupActionable = isOverallActionable && isFirstPendingGroup;

        return (
          <UserApprovalGroup
            key={approvalGroup.id}
            approvalGroup={approvalGroup}
            currentUserId={user.id}
            approveParams={approveParams}
            rejectParams={rejectParams}
            requestApprovalParams={requestApprovalParams}
            isActionable={isGroupActionable}
          />
        );
      })}
    </Accordion>
  );
};
